import { useEffect } from 'react';
import { useChatContext } from 'src/components/Chat/Chat.hook';
import { useAnalytics } from 'src/hooks/analytics/useAnalytics.hook';
import { useWorkspaceContext } from 'src/providers/WorkspaceProvider/Workspace.context';
import {
  UNSUPPORTED_NOTICE_EVENTS,
  tUnsupportedNotice,
} from './unsupportedNoticeAnalytics.constants';

type tUnsupportedNoticeAnalytics = {
  eventId: string;
  /** The chat the message belongs to: the channel is read from it. */
  chatId: string | undefined;
  /** The message's own date, so a re-read old message can be told apart. */
  createdAt: string | undefined;
  /** The notice this surface shows and counts, or null when it counts none. */
  notice: tUnsupportedNotice | null;
};

// Notices already reported in this tab, keyed `eventId:code`. A bubble mounts
// again when the chat is reopened or scrolled back into view: that is the same
// notice, not a new one. Reloading the page sends it again (INT-1691 R1).
const reportedNotices = new Set<string>();

/** Test-only: module state survives between the tests of one file. */
export function resetReportedUnsupportedNoticesForTests(): void {
  reportedNotices.clear();
}

/**
 * INT-1691: reports that a bubble showed a notice for content the chat cannot
 * show, once per message and code. Waits for the workspace (the analytics
 * helper drops events sent before it) and for the chat's channel: the 3063 and
 * 3064 notes render once while the channel is still unknown. The key is marked
 * only after both checks, so a notice painted during start-up is still sent.
 *
 * The channel comes from the selected chat, so the message must belong to it:
 * while the chat changes, a bubble of the previous chat can still be mounted
 * with `selectedChat` already pointing at the next one. That bubble waits, and
 * is sent with its own channel when its chat is open again.
 */
export default function useUnsupportedNoticeAnalytics({
  eventId,
  chatId,
  createdAt,
  notice,
}: tUnsupportedNoticeAnalytics): void {
  const { trackEvent } = useAnalytics();
  const { currentWorkspaceId } = useWorkspaceContext();
  const { selectedChat } = useChatContext();
  const channelType =
    chatId && selectedChat?.id === chatId
      ? selectedChat.integration
      : undefined;
  const code = notice?.code;
  const source = notice?.source;

  useEffect(() => {
    if (!code || !source || !eventId) return;
    if (!currentWorkspaceId || !channelType) return;
    const key = `${eventId}:${code}`;
    if (reportedNotices.has(key)) return;
    reportedNotices.add(key);
    trackEvent(UNSUPPORTED_NOTICE_EVENTS.SHOWN, {
      code,
      channel_type: channelType,
      source,
      message_created_at: createdAt || null,
    });
  }, [
    code,
    source,
    eventId,
    createdAt,
    currentWorkspaceId,
    channelType,
    trackEvent,
  ]);
}
